// src/calculators/civil.js — калкулатор „Граждански договор" (ПРОМТ-21).
// Сметката е в services/civil.js (чиста функция, с тестове), числата за
// годината — в config/insurance.config.json; тук е само екранът. Пише се за
// човек, който не е счетоводител: никакво съкращение без думи до него.
// Цитатите в колоната са сверени дословно в Ciela Net (23.10.2026).

import { el, clear, field, input } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import { state } from '../shared/state.js';
import { withLaw, hintify } from './law.js';
import { setChl55Prefill } from './prefill.js';
import { personPicker } from './personPick.js';
import { civilContract, grossFromNet } from '../../services/civil.js';
import { ACTIVITIES } from '../../services/chl55.js';
import { DISABILITY_THRESHOLD_EUR } from '../../services/rent.js';

const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const head = (label) => el('div', { class: 'calc-line', style: 'font-weight:600;margin-top:6px' }, el('span', { text: label }), el('span', { text: '' }));
const sub = (label, value) => el('div', { class: 'calc-line', style: 'padding-left:16px' }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });
const warnRow = (t) => el('div', { class: 'sev-warn', text: t });
const checkRow = (box, text) => el('div', { class: 'field' }, el('label', { class: 'choice' }, box, el('span', { text })));
const num = (x) => (Number(x) || 0).toFixed(2).replace('.', ',');
const pct = (x) => String(x).replace('.', ',') + ' %';
const dbg = (iso) => (iso ? iso.split('-').reverse().join('.') : '');

let _modeSeq = 0;

const STATEMENT_OFF = 'При самоосигуряващо се лице с декларация по чл. 43, ал. 5 ЗДДФЛ сметка за изплатени суми не се издава (чл. 45, ал. 6).';

export function buildCivilCalc() {
  const f = {
    gross: input({ type: 'number', step: '0.01', min: '0', placeholder: 'напр. 1000' }),
    paidOn: dateInput(localTodayIso()),
    activity: el('select', {}, ...ACTIVITIES.map((a) => el('option', { value: a.key, text: a.label + ' — ' + a.rate + ' %', selected: a.key === 'free25' }))),
    months: input({ type: 'number', step: '1', min: '1', max: '12', value: '1' }),
    before1960: el('input', { type: 'checkbox' }),
    elsewhere: el('input', { type: 'checkbox' }),
    otherIncome: input({ type: 'number', step: '0.01', min: '0', placeholder: 'по избор' }),
    selfInsured: el('input', { type: 'checkbox' }),
    pensioner: el('input', { type: 'checkbox' }),
    pensionerWants: el('input', { type: 'checkbox' }),
    reduced: el('input', { type: 'checkbox' }),
    ytd: input({ type: 'number', step: '0.01', min: '0', placeholder: 'от началото на годината' }),
    q4: el('input', { type: 'checkbox' }),
  };
  f.activity.style.maxWidth = '100%';

  // Бруто или нето (ПРОМТ-21а): клиентът често знае само „колко да получи
  // човекът на ръка". Изборът сменя само смисъла на числото в полето —
  // самото число остава, за да се сравнят двата случая с едно превключване.
  const modeName = 'civil-amount-mode-' + (++_modeSeq);
  const modeGross = el('input', { type: 'radio', name: modeName, value: 'gross', checked: true });
  const modeNet = el('input', { type: 'radio', name: modeName, value: 'net' });
  const modeChoice = (box, text) => el('label', { class: 'choice', style: 'padding:6px 10px;border:1px solid var(--line, #d1d5db);border-radius:8px' }, box, el('span', { text }));
  const modeField = el('div', { class: 'field', role: 'radiogroup', 'aria-label': 'Каква сума въвеждате' },
    el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' },
      modeChoice(modeGross, 'Въвеждам брутна сума (по договора)'),
      modeChoice(modeNet, 'Въвеждам нетна сума (колко да получи човекът)')));
  const isNet = () => modeNet.checked;

  // Условните полета — показват се само когато имат смисъл.
  const elsewhereRow = hintify(checkRow(f.elsewhere, 'Лицето е осигурено и другаде този месец (трудов договор, договор за управление…)'),
    'чл. 4, ал. 3, т. 6 КСО', 'Осигурен на друго основание през месеца — осигуровки се дължат независимо от размера на възнаграждението.');
  const otherField = hintify(field('Осигурителен доход на другото място за месеца', f.otherIncome, { size: 'm' }),
    'чл. 6, ал. 11 КСО', 'Ако е над максимума, тук не се дължат осигуровки — вноските са върху сбора, но до максималния месечен размер.');
  const selfRow = hintify(checkRow(f.selfInsured, 'Самоосигуряващо се лице (с подадена декларация)'),
    'чл. 43, ал. 5 ЗДДФЛ', 'Свободна професия, занаятчия, ЕТ и др.: с писмена декларация възложителят не удържа нито осигуровки, нито данък.');
  const pensionerRow = hintify(checkRow(f.pensioner, 'Лицето е пенсионер'),
    'чл. 4, ал. 6 КСО', 'Пенсионерите по граждански договор се осигуряват за пенсия по свое желание.');
  const wantsRow = checkRow(f.pensionerWants, 'Пенсионерът желае да се осигурява за пенсия');
  const reducedRow = checkRow(f.reduced, 'Лицето е с намалена работоспособност ≥ 50 % (ТЕЛК/НЕЛК; праг ' + num(DISABILITY_THRESHOLD_EUR) + ' €)');
  const ytdField = field('Облагаем доход от всички източници от началото на годината', f.ytd, { size: 'm' });
  const q4Row = hintify(checkRow(f.q4, 'Лицето писмено е поискало удържане на данък (чл. 65, ал. 13)'),
    'чл. 65, ал. 13 ЗДДФЛ', 'За IV тримесечие данък не се удържа, освен ако лицето писмено поиска; тогава се внася до 31 януари.');
  const monthsNote = el('div', { class: 'calc-hint-line' });

  // Полето за сумата: етикетът се сменя в същия <label> (връзката for/id и
  // иконката § остават) — снимката за досието и достъпността не се чупят.
  const grossField = hintify(field('Брутна сума по договора', f.gross, { size: 'm' }),
    'чл. 29, ал. 1 ЗДДФЛ', 'Сумата преди удръжките; от нея се приспадат нормативно признатите разходи — не е нужно да ги доказвате.');
  function syncAmountLabel() {
    const lbl = grossField.querySelector('label');
    const txt = lbl && [...lbl.childNodes].find((x) => x.nodeType === 3);
    if (txt) txt.nodeValue = isNet() ? 'Нето за получаване' : 'Брутна сума по договора';
    f.gross.placeholder = isNet() ? 'напр. 800' : 'напр. 1000';
  }

  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  let last = null; // последната сметка — за печата и за чл. 55

  const person = personPicker(() => recompute());

  function inputData() {
    return {
      gross: Number(f.gross.value) || 0,
      activity: f.activity.value,
      paidOn: f.paidOn.dataset.iso || '',
      months: Number(f.months.value) || 1,
      birthBefore1960: f.before1960.checked,
      insuredElsewhere: f.elsewhere.checked,
      otherIncome: Number(f.otherIncome.value) || 0,
      selfInsured: f.selfInsured.checked,
      pensioner: f.pensioner.checked,
      pensionerWantsInsurance: f.pensionerWants.checked,
      reducedCapacity: f.reduced.checked,
      ytdTaxableIncome: Number(f.ytd.value) || 0,
      quarter4Withholding: f.q4.checked,
    };
  }

  const printBtn = el('button', { class: 'btn', type: 'button', text: 'Печат — сметка за изплатени суми (PDF)', onClick: () => printPdf() });
  const printHint = el('span', { style: 'font-size:12px;color:var(--muted)' });
  const toChl55 = el('button', { class: 'btn ghost', type: 'button', text: 'Към декларацията по чл. 55', onClick: () => {
    if (!last) { toast('Първо попълнете сумата и датата.', true); return; }
    setChl55Prefill({
      quarter: last.quarter, year: last.year,
      row: { kind: 'other43', amount: last.gross, activity: last.activity, contributions: last.personTotal, wantsQ4: f.q4.checked },
    });
    window.openCalc('chl55');
  } });

  function recompute() {
    clear(result); clear(notesBox);
    const d = inputData();
    const isQ4 = Number(String(d.paidOn).slice(5, 7)) >= 10;
    const self = d.selfInsured;
    elsewhereRow.style.display = self ? 'none' : '';
    otherField.style.display = (!self && d.insuredElsewhere) ? '' : 'none';
    pensionerRow.style.display = self ? 'none' : '';
    wantsRow.style.display = (!self && d.pensioner) ? '' : 'none';
    ytdField.style.display = d.reducedCapacity ? '' : 'none';
    q4Row.style.display = (!self && isQ4) ? '' : 'none';
    monthsNote.textContent = d.months > 1
      ? 'Сумата се разделя поравно на ' + d.months + ' месеца (чл. 3, ал. 1 НЕВДПОВ — по отработените дни; при неравни дни сметнете ръчно).'
      : '';
    monthsNote.style.display = d.months > 1 ? '' : 'none';
    printBtn.disabled = self;
    printHint.textContent = self ? STATEMENT_OFF : 'Сметката по чл. 45, ал. 4 ЗДДФЛ се дава на лицето при всяко плащане.';
    // Скритото поле не бива да влияе на сметката (и на снимката за досието).
    if (self) { d.insuredElsewhere = false; d.pensioner = false; d.quarter4Withholding = false; }
    if (!d.pensioner) d.pensionerWantsInsurance = false;
    if (!isQ4) d.quarter4Withholding = false;

    last = null;
    syncAmountLabel();
    if (d.gross <= 0 || !d.paidOn) {
      result.appendChild(el('div', { class: 'calc-line', text: isNet()
        ? 'Въведете колко да получи човекът на ръка и датата на плащане.'
        : 'Въведете брутната сума по договора и датата на плащане.' }));
      return;
    }
    let r;
    try {
      if (isNet()) {
        const n = Object.assign({}, d, { net: d.gross });
        delete n.gross;
        r = grossFromNet(n);
      } else {
        r = civilContract(d);
      }
    } catch (e) {
      result.appendChild(warnRow('⚠ ' + e.message));
      return;
    }
    last = r;
    const m = (x) => money(x, r.currency);

    if (person.name()) result.appendChild(line('Получател на дохода', person.name()));
    if (isNet()) {
      // Сметнатото бруто е отговорът на въпроса — най-отгоре и отличено.
      result.appendChild(line('Брутна сума по договора (сметната)', m(r.solvedGross), true));
      if (!r.exact) result.appendChild(warnRow('⚠ ' + r.solveNote));
      r.alternatives.forEach((a) => result.appendChild(el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
        noteRow(a.note),
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Покажи с тази сума', onClick: () => {
          modeGross.checked = true;
          f.gross.value = String(a.gross);
          recompute();
        } }))));
    }
    result.appendChild(line('Брутно възнаграждение', m(r.gross)));
    result.appendChild(line('− Нормативно признати разходи ' + pct(r.nprRate), m(r.npr)));
    result.appendChild(line(r.insured ? '= Осигурителен доход' : '= Доход след разходите', m(r.insurableBase)));

    if (r.insured) {
      if (r.capped) result.appendChild(line('Основа за осигуровките — ограничен до ' + m(r.insurableIncome), m(r.insurableIncome)));
      result.appendChild(line('Максимален осигурителен доход за месеца', m(r.maxIncome)));
      result.appendChild(head('Осигуровки за сметка на лицето'));
      r.rows.forEach((x) => result.appendChild(sub(x.label + ' ' + pct(x.personRate), m(x.personAmount))));
      result.appendChild(sub('Общо за лицето', m(r.personTotal)));
    } else {
      result.appendChild(line('Осигуровки', 'не се дължат'));
      // Ясното „защо" — първата бележка на сметката.
      result.appendChild(el('div', { class: 'calc-why', style: 'font-size:13px;padding:4px 0 6px', text: r.notes[0] }));
    }

    result.appendChild(line(r.personTotal > 0 ? 'Данъчна основа (' + num(r.insurableBase) + ' − ' + num(r.personTotal) + ')' : 'Данъчна основа', m(r.taxableAfterContrib)));
    if (r.taxBase !== r.taxableAfterContrib) result.appendChild(line('Данъчна основа след прага за ТЕЛК', m(r.taxBase)));
    result.appendChild(line('Данък ' + pct(r.taxRate), m(r.tax)));
    result.appendChild(line('Нето за получаване', m(r.net), true));

    if (r.insured) {
      result.appendChild(head('Осигуровки за сметка на възложителя'));
      r.rows.forEach((x) => result.appendChild(sub(x.label + ' ' + pct(x.payerRate), m(x.payerAmount))));
      result.appendChild(sub('Общо за възложителя', m(r.payerTotal)));
    }
    result.appendChild(line('Общ разход на възложителя', m(r.cost), true));
    if (r.deadlines.insurance) result.appendChild(line('Срок за осигуровките', dbg(r.deadlines.insurance)));
    if (r.deadlines.tax) result.appendChild(line('Срок за данъка', dbg(r.deadlines.tax)));

    r.notes.forEach((n, i) => { if (r.insured || i > 0) notesBox.appendChild(noteRow(n)); });
    notesBox.appendChild(noteRow('Не се смятат тук: избор по чл. 4б КСО (прехвърлени в НОИ — вноската за фонд „Пенсии" е с 5 пункта повече вместо УПФ); чуждестранни лица; възложител — физическо лице; авансови плащания по договора.'));
  }

  // ---- Печат на сметката за изплатени суми ----
  async function printPdf() {
    if (!last) { toast('Първо попълнете сумата и датата.', true); return; }
    if (!last.statementAllowed) { toast(STATEMENT_OFF, true); return; }
    if (!state().activeCompany) { toast('Изберете активна фирма — тя е платецът в сметката.', true); return; }
    try {
      const personId = await person.ensureId();
      const res = await fetch('/api/civil/pdf', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        // Сървърът получава БРУТОТО (при „нетна" — сметнатото), не нетото:
        // сметката за изплатени суми е по договорената брутна сума.
        body: JSON.stringify({ company_id: state().activeCompany, person_id: personId, input: Object.assign(inputData(), { gross: last.gross }) }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Грешка при печата.'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = el('a', { href: url, download: 'smetka-izplateni-sumi-' + last.paidOn + '.pdf' });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast('Сметката за изплатени суми е свалена.');
    } catch (e) { toast(e.message, true); }
  }

  Object.values(f).forEach((n) => { n.addEventListener('input', recompute); n.addEventListener('change', recompute); });
  [modeGross, modeNet].forEach((n) => n.addEventListener('change', recompute));
  person.load();

  const panel = el('div', { class: 'panel calc-panel', style: 'max-width:860px' },
    el('h3', { class: 'calc-title', text: 'Граждански договор — осигуровки, данък и нето' }),
    modeField,
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' },
      grossField,
      hintify(field('Дата на плащане', f.paidOn, { size: 'm' }),
        'чл. 7, ал. 6 КСО', 'Осигуровките се внасят до 25-о число на месеца след месеца на изплащане.'),
      hintify(field('За колко месеца е възнаграждението', f.months, { size: 'm' }),
        'чл. 3, ал. 1 НЕВДПОВ', 'Възнаграждение за минало време се разпределя за отработените дни, през които трудът е положен.')),
    monthsNote,
    hintify(field('Вид дейност', f.activity),
      'чл. 29, ал. 1 ЗДДФЛ', '25 на сто — граждански договор и свободна професия; 40 на сто — авторски, занаят, адвокат и нотариус; 60 на сто — регистриран земеделски стопанин.'),
    hintify(checkRow(f.before1960, 'Лицето е родено преди 1960 г.'),
      'чл. 127, ал. 1 КСО', 'Допълнителна пенсия в универсален пенсионен фонд се дължи само за родените след 31 декември 1959 г.'),
    elsewhereRow,
    otherField,
    selfRow,
    pensionerRow,
    wantsRow,
    reducedRow,
    ytdField,
    q4Row,
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end' },
      field('Получател на дохода (лице от „Досиета")', person.sel),
      person.nameField),
    result,
    notesBox,
    el('div', { style: 'margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;align-items:center' }, printBtn, toChl55, printHint));

  recompute();

  const wrapped = withLaw(panel, {
    when: [
      'Фирмата плаща на човек по граждански договор — колко да удържи, колко да му преведе и колко струва това на фирмата.',
      'Осигуровки се дължат, ако лицето е осигурено и другаде този месец (например на трудов договор) — при всяка сума; ако не е — само когато сумата след разходите е поне минималната заплата.',
      'Самоосигуряващо се лице (свободна професия, занаятчия) с декларация — фирмата не удържа нищо, лицето само внася осигуровките и данъка.',
      'Пенсионер — за пенсия се осигурява само ако иска; здравната се дължи по общото правило.',
      'Плащане през октомври–декември — данък не се удържа, освен ако лицето писмено поиска.',
    ],
    quotes: [
      { ref: 'чл. 4, ал. 3, т. 5 и 6 КСО', text: '… 5. лицата, които полагат труд без трудово правоотношение и получават месечно възнаграждение, равно или над една минимална работна заплата, след намаляването му с разходите за дейността, ако не са осигурени на друго основание през съответния месец; 6. лицата, които полагат труд без трудово правоотношение, които са осигурени на друго основание през съответния месец, независимо от размера на полученото възнаграждение.' },
      { ref: 'чл. 6, ал. 4 КСО', text: 'Възнаграждението за лицата по чл. 4, ал. 3, т. 5 и 6, върху което се дължат осигурителни вноски, се определя след намаляването му с разходите за дейността, определени по реда на Закона за данъците върху доходите на физическите лица.' },
      { ref: 'чл. 4, ал. 6 КСО', text: 'Лицата по ал. 3, т. 1, 2, 4 - 6, на които е отпусната пенсия, се осигуряват по свое желание.' },
      { ref: 'чл. 40, ал. 1, т. 3 ЗЗО', text: 'а) ако не се осигуряват по реда на т. 1, 2 и 2а и получават възнаграждение, равно или по-голямо от минималната работна заплата за страната, върху облагаемия доход, след намаляването му с разходите за дейността; когато е получено възнаграждение под минималната работна заплата за страната, след намаляването му с разходите за дейността, осигуряването се извършва по реда на ал. 5; б) ако са осигурени по реда на т. 1 и 2а, осигурителните вноски се внасят върху облагаемия доход, след намаляването му с разходите за дейността, независимо от размера на полученото възнаграждение; в) осигурителните вноски се внасят в съотношението по т. 1 от възложителя до 25-о число на месеца, следващ месеца на изплащане на възнаграждението;' },
      { ref: 'чл. 7, ал. 6 КСО', text: 'Осигурителните вноски за лицата, които работят без трудово правоотношение, се внасят от осигурителя до 25-о число на месеца, следващ месеца на изплащане на възнаграждението.' },
      { ref: 'чл. 43, ал. 1 ЗДДФЛ', text: 'Лице, придобило доход от стопанска дейност по чл. 29, дължи авансово данък върху разликата между облагаемия доход и вноските, които самоосигуряващото се лице е задължено да прави за своя сметка за месеците на тримесечието, през което е придобит облагаемият доход, а ако лицето не е самоосигуряващо се - между облагаемия доход и удържаните задължителни осигурителни вноски.' },
      { ref: 'чл. 43, ал. 5 ЗДДФЛ', text: 'Алинея 4 не се прилага, когато лицето, придобиващо дохода, е самоосигуряващо се лице по смисъла на Кодекса за социално осигуряване и декларира това обстоятелство с писмена декларация пред платеца на дохода.' },
      { ref: 'чл. 45, ал. 4 ЗДДФЛ (изм. ДВ, бр. 98/2018)', text: 'Предприятие или самоосигуряващо се лице по смисъла на Кодекса за социално осигуряване – платец на доходи по чл. 29 или 31, издава за изплатените доходи и удържания данък по реда на чл. 43 и 44 сметка за изплатени суми по образец, която предоставя на лицето, придобило дохода, или на упълномощено от него лице.' },
      { ref: 'чл. 11, ал. 1 Наредба за обществено осигуряване на самоосигуряващите се лица (НООСЛБГРЧМЛ)', text: 'За лицата по чл. 4, ал. 3, т. 1, 2 и 4 от Кодекса за социално осигуряване при изплащане на възнаграждение за работа без трудово правоотношение осигурителни вноски не се внасят от възложителя на работата и не се удържат от възнаграждението.' },
    ],
  });
  // Мостът към „Запази в досие" (по модела на чл. 55): избраното лице казва
  // къде да се запише без диалог; ново име първо създава досие.
  wrapped._dossierPreset = async () => {
    const id = await person.ensureId();
    return id ? { personId: id, name: person.name() } : null;
  };
  return wrapped;
}
